import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/errors";
import { AuthLayout } from "@/pages/auth/AuthLayout";
import { Field } from "@/components/common/FormField";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GoogleSoonButton } from "@/pages/auth/GoogleSoonButton";

export default function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { login, sessionExpired } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (location.state?.resetDone) toast.success(t("auth.resetDone"));
  }, [location.state, t]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const data = await login(email.trim(), password);
      const orgs = data.organizations || [];
      const from = location.state?.from;
      if (data.user?.is_platform_admin && !orgs.length) navigate("/app/admin", { replace: true });
      else if (!orgs.length) navigate("/onboarding", { replace: true });
      else navigate(from && from.startsWith("/app") ? from : "/app", { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title={t("auth.loginTitle")}
      subtitle={t("auth.loginSubtitle")}
      footer={
        <>
          {t("auth.noAccount")}{" "}
          <Link to="/register" className="og-focus font-medium text-primary hover:underline" data-testid="login-register-link">{t("auth.register")}</Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-4" noValidate data-testid="login-form">
        {sessionExpired && !error && (
          <Alert className="border-amber-200 bg-amber-50 text-amber-950"><AlertDescription>{t("auth.sessionExpired")}</AlertDescription></Alert>
        )}
        {error && (
          <Alert className="border-rose-200 bg-rose-50 text-rose-900" data-testid="login-error"><AlertDescription>{error}</AlertDescription></Alert>
        )}
        <Field id="login-email" label={t("auth.email")}>
          <Input id="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("auth.emailPlaceholder")} data-testid="login-email-input" />
        </Field>
        <Field id="login-password" label={t("auth.password")}>
          <div className="relative">
            <Input id="login-password" type={show ? "text" : "password"} autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" data-testid="login-password-input" />
            <button type="button" onClick={() => setShow((s) => !s)} className="og-focus absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground" aria-label={show ? "Masquer" : "Afficher"} data-testid="login-toggle-password">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>
        <div className="flex items-center justify-end">
          <Link to="/forgot-password" className="og-focus text-sm font-medium text-primary hover:underline" data-testid="login-forgot-password-link">{t("auth.forgot")}</Link>
        </div>
        <Button type="submit" className="h-10 w-full" disabled={busy || !email || !password} data-testid="login-submit-button">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {t("auth.login")}
        </Button>
        <GoogleSoonButton />
      </form>
    </AuthLayout>
  );
}
